import type { IRatingMatrixFrameworkGradeScore } from '__generated__';
import { baseApi, gradeScoreListTag } from 'store/api';
import {
  API,
  APIRead,
  asPatchBody,
  getAllDynamic,
  normalizeError,
  type PatchBody,
} from 'utils/api-client';
import {
  buildGradeScoreTemplateFilter,
  getScoreSwapPatches,
  type ScoreSwapPatch,
} from './helpers';

export const ratingMatrixFrameworkGradeScoreApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getGradeScores: builder.query<
      IRatingMatrixFrameworkGradeScore[],
      { frameworkTemplateID: number }
    >({
      async queryFn({ frameworkTemplateID }) {
        try {
          const { value } = await getAllDynamic<IRatingMatrixFrameworkGradeScore>(
            (body) => APIRead.api.ratingMatrixFrameworkGradeScoreGetAllDynamicCreate(body),
            {
              filter: buildGradeScoreTemplateFilter(frameworkTemplateID),
              orderBy: 'numericScore asc',
            },
          );

          return { data: value };
        } catch (error) {
          return { error: normalizeError(error) };
        }
      },
      providesTags: (_result, _error, arg) => [gradeScoreListTag(arg.frameworkTemplateID)],
    }),

    createGradeScore: builder.mutation<
      IRatingMatrixFrameworkGradeScore,
      Partial<IRatingMatrixFrameworkGradeScore> & { frameworkTemplateID: number }
    >({
      async queryFn(body) {
        try {
          const { data } = await API.api.ratingMatrixFrameworkGradeScoreCreateCreate(body);
          return { data };
        } catch (error) {
          return { error: normalizeError(error) };
        }
      },
      invalidatesTags: (_result, _error, arg) => [gradeScoreListTag(arg.frameworkTemplateID)],
    }),

    patchGradeScore: builder.mutation<
      string,
      {
        id: number;
        frameworkTemplateID: number;
        patch: PatchBody<IRatingMatrixFrameworkGradeScore>;
      }
    >({
      async queryFn({ id, patch }) {
        try {
          const { data } = await API.api.ratingMatrixFrameworkGradeScorePatchPartialUpdate(
            { id },
            asPatchBody(patch),
          );
          return { data };
        } catch (error) {
          return { error: normalizeError(error) };
        }
      },
      invalidatesTags: (_result, _error, arg) => [gradeScoreListTag(arg.frameworkTemplateID)],
    }),

    deleteGradeScore: builder.mutation<
      IRatingMatrixFrameworkGradeScore,
      { id: number; frameworkTemplateID: number }
    >({
      async queryFn({ id }) {
        try {
          const { data } = await API.api.ratingMatrixFrameworkGradeScoreDeleteDelete({ id });
          return { data };
        } catch (error) {
          return { error: normalizeError(error) };
        }
      },
      invalidatesTags: (_result, _error, arg) => [gradeScoreListTag(arg.frameworkTemplateID)],
    }),

    swapGradeScores: builder.mutation<
      void,
      {
        frameworkTemplateID: number;
        grades: IRatingMatrixFrameworkGradeScore[];
        fromIndex: number;
        toIndex: number;
      }
    >({
      async queryFn({ grades, fromIndex, toIndex }) {
        try {
          const patches = getScoreSwapPatches(grades, fromIndex, toIndex);

          if (!patches) {
            return { data: undefined };
          }

          await Promise.all(
            patches.map((patch: ScoreSwapPatch) =>
              API.api.ratingMatrixFrameworkGradeScorePatchPartialUpdate(
                { id: patch.id },
                asPatchBody({
                  gradeLabel: patch.gradeLabel,
                  // numericScore: patch.numericScore,
                }),
              ),
            ),
          );

          return { data: undefined };
        } catch (error) {
          return { error: normalizeError(error) };
        }
      },
      invalidatesTags: (_result, _error, arg) => [gradeScoreListTag(arg.frameworkTemplateID)],
    }),
  }),
});

export const {
  useGetGradeScoresQuery,
  useCreateGradeScoreMutation,
  usePatchGradeScoreMutation,
  useDeleteGradeScoreMutation,
  useSwapGradeScoresMutation,
} = ratingMatrixFrameworkGradeScoreApi;
